import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { initializeAuth, initializeRehydration } from '@fog-chess/core';
import { queryClient } from './lib/reactQueryClient';
import './index.css';

/**
 * Boot sequence:
 *  1. initializeAuth() - hydrate persisted user + trigger silent token refresh
 *  2. initializeRehydration() - if authenticated user has active match,
 *     connect socket, emit reconnect:attempt, block until game:state received
 *  3. Mount app under QueryClientProvider + BrowserRouter
 */
async function boot(): Promise<void> {
  try {
    // Step 1: Hydrate auth (restores user from localStorage, triggers token refresh via cookie)
    await initializeAuth();
    
    // Step 2: Execute strict rehydration pipeline for active matches
    await initializeRehydration();
    
    // Step 3: Mount React app
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </React.StrictMode>,
    );
  } catch (error) {
    // Boot failure must not leave app stuck
    console.error('[boot] Fatal startup error:', error);
    
    // Render error boundary or fallback
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
}

boot();
